import { createContext, Suspense, use, useCallback, useEffect, useRef } from 'react';
import { type FieldValues, FormProvider, useForm } from 'react-hook-form';

import { message, Skeleton } from 'antd';

import { STEP_VALIDATORS, type StepName as DomainStepName } from '@eduzz-registry-360/validators';
import { zodResolver } from '@hookform/resolvers/zod';
import z from 'zod';

import { FlowContext } from '@/components/Flow';
import { type GetErrorInfo, type Loading } from '@/hooks/useLoading';
import { useLoadingCleaner } from '@/hooks/useLoadingCleaner';
import { useSaveStep } from '@/hooks/useSaveStep';
import { useZustandSync } from '@/hooks/useZustandSync';
import useRegistryStore, { registryStore } from '@/stores/registry';

import { Content } from './Content';
import { Footer } from './Footer';
import { Label } from './Label';
import { allSteps } from './registry';
import { StepScopeProvider } from './scope';
import { Subtitle } from './Subtitle';
import { Title } from './Title';
import { type Step as StepType } from '../Flow/types';
import { WizardContext } from '../Wizard';
import { FlowListContext } from '../Wizard/FlowList';

function inferFieldDefault(fieldSchema: z.ZodType): string | boolean | unknown[] {
  const inner =
    fieldSchema instanceof z.ZodOptional || fieldSchema instanceof z.ZodNullable
      ? (fieldSchema.unwrap() as z.ZodType)
      : fieldSchema;

  if (inner instanceof z.ZodBoolean) {
    return false;
  }

  if (inner instanceof z.ZodArray) {
    return [];
  }

  return '';
}

function extractShape(schema: z.ZodType): Record<string, z.ZodType> | null {
  if (schema instanceof z.ZodObject) {
    return schema.shape;
  }

  if (!(schema instanceof z.ZodUnion || schema instanceof z.ZodDiscriminatedUnion)) {
    return null;
  }

  const shapes = schema.options
    .filter((opt): opt is z.ZodObject<z.ZodRawShape> => opt instanceof z.ZodObject)
    .map(opt => opt.shape);

  if (!shapes.length) {
    return null;
  }

  const merged: Record<string, z.ZodType> = Object.assign({}, ...shapes.reverse());
  return Object.keys(merged).length > 0 ? merged : null;
}

function defaultValuesForValidator(validator: z.ZodType<FieldValues, FieldValues>): FieldValues {
  const shape = extractShape(validator);

  if (!shape) {
    return {};
  }

  const slice = registryStore.getState().registry as Record<string, unknown>;
  const defaults: FieldValues = {};

  for (const [key, fieldSchema] of Object.entries(shape)) {
    const persisted = slice[key];
    const fallback = inferFieldDefault(fieldSchema);

    if (Array.isArray(fallback)) {
      defaults[key] = Array.isArray(persisted) ? persisted : fallback;
    } else {
      defaults[key] = typeof persisted === typeof fallback ? persisted : fallback;
    }
  }

  return defaults;
}

const scrollToFirstFieldError = (names: string[], setFocus: (name: string) => void): void => {
  const elements = names
    .map(name => ({
      name,
      element: document.querySelector(
        `[name="${CSS.escape(name)}"], [data-field-anchor="${CSS.escape(name)}"]`
      ) as HTMLElement | null
    }))
    .filter((entry): entry is { name: string; element: HTMLElement } => Boolean(entry.element));

  elements.sort((a, b) => {
    const position = a.element.compareDocumentPosition(b.element);

    if (position & Node.DOCUMENT_POSITION_FOLLOWING) {
      return -1;
    }

    if (position & Node.DOCUMENT_POSITION_PRECEDING) {
      return 1;
    }

    return 0;
  });

  const first = elements[0];

  if (!first) {
    return;
  }

  requestAnimationFrame(() => {
    first.element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setFocus(first.name);
  });
};

type LoadingOptions<E = unknown> = Omit<Loading, 'lastFlow' | 'action' | 'getErrorInfo'> & {
  error: GetErrorInfo<E>;
};

export const StepContext = createContext<{
  next: <E = unknown>(action?: () => Promise<unknown>, loading?: LoadingOptions<E>) => void;
  previous: () => void;
}>({
  next: () => {},
  previous: () => {}
});

export type StepProps = StepType;

const isBackendStep = (name: string): name is DomainStepName => name in STEP_VALIDATORS;

export const Step = ({ name, untraceable }: StepProps) => {
  const { triggerLoading, currentFlowList, pendingFieldErrors, clearPendingFieldErrors } = use(WizardContext);
  const { name: flowListName } = use(FlowListContext);
  const { nextStep, previousStep } = use(FlowContext);
  const scopePrefix = `${flowListName ?? 'flow'}-${name}`;
  const saveStep = useSaveStep();
  const nextLocked = useRef(false);
  const previousFlowListRef = useRef(currentFlowList);
  const { bankAccountDocument, personType } = useRegistryStore(({ registry }) => ({
    bankAccountDocument: registry.bankAccountDocument,
    personType: registry.personType
  }));

  const currentStep = allSteps[name];
  const resolvedValidator = (currentStep.validator ?? z.object({})) as z.ZodType<FieldValues, FieldValues>;

  useLoadingCleaner();

  const form = useForm({
    resolver: zodResolver(resolvedValidator),
    mode: 'onTouched',
    defaultValues: defaultValuesForValidator(resolvedValidator)
  });

  useEffect(() => {
    if (previousFlowListRef.current !== currentFlowList) {
      form.setValue('bankAccountDocument', bankAccountDocument);
      form.setValue('personType', personType);
    }
  }, [currentFlowList, form, bankAccountDocument, personType]);

  const {
    formState: { errors }
  } = form;

  useEffect(() => {
    scrollToFirstFieldError(Object.keys(errors), name => form.setFocus(name));
  }, [errors, form]);

  useEffect(() => {
    if (!pendingFieldErrors) {
      return;
    }

    const shape = extractShape(resolvedValidator);
    const applicable = Object.entries(pendingFieldErrors).filter(([key]) => shape && key in shape);

    if (!applicable.length) {
      return;
    }

    for (const [name, message] of applicable) {
      form.setValue(name, '');
      form.setError(name, { type: 'server', message });
    }

    scrollToFirstFieldError(
      applicable.map(([name]) => name),
      name => form.setFocus(name)
    );

    clearPendingFieldErrors();
  }, [pendingFieldErrors, clearPendingFieldErrors, resolvedValidator, form]);

  const syncMapper = (values: FieldValues): FieldValues => {
    const { validator } = currentStep;

    if (!validator) {
      return {};
    }

    const parsed = validator.safeParse(values);

    if (!parsed.success) {
      return {};
    }

    return parsed.data as FieldValues;
  };

  useZustandSync<FieldValues>(form.control, syncMapper);

  const persistStep = useCallback(async () => {
    if (untraceable || !isBackendStep(name)) {
      return;
    }

    await saveStep.mutateAsync({
      step: name,
      data: registryStore.getState().registry as unknown as Record<string, unknown>
    });
  }, [name, untraceable, saveStep]);

  const next = useCallback(
    async (action?: () => Promise<unknown>, loading?: LoadingOptions) => {
      if (nextLocked.current) {
        return;
      }

      nextLocked.current = true;
      const isValid = await form.trigger();

      nextLocked.current = false;

      if (!isValid) {
        return;
      }

      const runActionAndPersist = async () => {
        if (action) {
          await action();
        }

        await persistStep();
      };

      if (loading) {
        triggerLoading({ ...loading, action: runActionAndPersist });
        return;
      }

      try {
        await runActionAndPersist();
      } catch (error) {
        message.error('Não foi possível salvar esta etapa. Verifique sua conexão e tente novamente.');
        throw error;
      }

      nextStep();
    },
    [form, nextStep, persistStep, triggerLoading]
  );

  const previous = useCallback(() => {
    previousStep();
  }, [previousStep]);

  return (
    <StepContext.Provider value={{ next, previous }}>
      <FormProvider {...form}>
        <StepScopeProvider prefix={scopePrefix}>
          <section className='flex flex-1 flex-col'>
            {
              <Suspense fallback={<Skeleton active />}>
                <currentStep.component />
              </Suspense>
            }
          </section>
        </StepScopeProvider>
      </FormProvider>
    </StepContext.Provider>
  );
};

Step.Label = Label;
Step.Title = Title;
Step.Subtitle = Subtitle;
Step.Content = Content;
Step.Footer = Footer;
